import { Box, Edges, Line } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import { MeasureKind, UnitsByMeasure } from 'convert';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { EInteractionMode, ESubMenu, EUnitType } from '../../@types/Enums';
import { Side } from '../../@types/Side';
import useDraggableMesh from '../../hooks/useDraggableMesh';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import RadiatorModel from '../../models/standard/RadiatorModel';
import StandardWallModel from '../../models/standard/StandardWallModel';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { INTERNAL_TO_CM_FACTOR } from '../../utils/constants';
import { convertTo } from '../../utils/conversionUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { snapRadiatorToWall } from '../../utils/wallElementSnapping';
import RadiatorLengthIndicator from './indicators/RadiatorLengthIndicator';

export { snapRadiatorToWall };

export interface RadiatorProps {
  onClick: () => void;
  radiator: RadiatorModel;
  wallModel: StandardWallModel;
  selected: boolean;
  onWallUpdate: (wallUuid: string, wall: StandardWallModel, action?: EHistoryAction) => void;
  radiatorIndex: number;
  wallIndex: number;
  side: Side;
}

const Radiator = ({ onClick, radiator, wallModel, selected, onWallUpdate, radiatorIndex, wallIndex, side }: RadiatorProps): JSX.Element => {
  const groupRef = useRef<THREE.Group>(null);
  const { length: width, depth: thickness, angle } = radiator;
  const [hovered, setHovered] = useState(false);
  const [radiatorWidth, setRadiatorWidth] = useState<number>(width);
  const { interactionMode, setCurrentlyActiveSubMenu } = useToolbarContext();
  const { showRadiatorLength } = useMultiSelectToggleContext();
  const { setRadiatorDepthAttribute, setRadiatorLengthAttribute } = useBuilding();
  const { unitType } = useUnitContext();

  const lengthCm = radiator.length * INTERNAL_TO_CM_FACTOR;
  const depthCm = radiator.depth * INTERNAL_TO_CM_FACTOR;
  const lengthValue =
    unitType === EUnitType.METRIC
      ? parseFloat(lengthCm.toFixed(3))
      : Math.round(convertTo(lengthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
  const depthValue =
    unitType === EUnitType.METRIC
      ? parseFloat(depthCm.toFixed(3))
      : Math.round(convertTo(depthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
  const unit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  useEffect(() => {
    setRadiatorLengthAttribute(wallIndex, side, radiatorIndex, lengthValue, unit);
    setRadiatorDepthAttribute(wallIndex, side, radiatorIndex, depthValue, unit);
  }, [setRadiatorLengthAttribute, setRadiatorDepthAttribute, wallIndex, side, radiatorIndex, lengthValue, depthValue, unit]);

  useEffect(() => {
    setRadiatorWidth(width);
  }, [width]);

  const meshColor = useMemo(() => (selected ? '#009fe3' : hovered ? '#888888' : 'white'), [hovered, selected]);

  const verticalLinePositions = useMemo(() => {
    const desiredSpacing = 0.3;
    const numLines = Math.max(Math.floor(radiatorWidth / desiredSpacing) - 1, 0);
    if (numLines === 0) return [];
    const spacing = radiatorWidth / (numLines + 1);
    return Array.from({ length: numLines }, (_, i) => {
      const x = -radiatorWidth / 2 + spacing * (i + 1);
      return new THREE.Vector3(x, 0, 0);
    });
  }, [radiatorWidth]);

  const constraintFunction = useCallback(
    (newPosition: THREE.Vector3): THREE.Vector3 => snapRadiatorToWall(wallModel, newPosition, radiatorWidth, radiator.depth, radiator.nicheDepth ?? 0, side),
    [wallModel, radiatorWidth, radiator.depth, radiator.nicheDepth, side],
  );

  const updateRadiatorPosition = useCallback(
    (newPosition: THREE.Vector3): void => {
      radiator.position.copy(newPosition);
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.RADIATOR_POSITION_CHANGED);
    },
    [radiator, onWallUpdate, wallModel],
  );

  useDraggableMesh(groupRef, constraintFunction, updateRadiatorPosition, interactionMode !== EInteractionMode.MOVE, interactionMode !== EInteractionMode.MOVE);

  const handlePointerEvents = (event: ThreeEvent<PointerEvent>, callback: (e: ThreeEvent<PointerEvent>) => void) => {
    event.stopPropagation();
    setCurrentlyActiveSubMenu(ESubMenu.NONE);
    if (interactionMode !== EInteractionMode.MOVE) {
      callback && callback(event);
    }
  };

  const selectedZ = selected ? 5 : 0;
  const selectedRenderOrder = selected ? RENDER_ORDERS.lengthIndicator + 1 : 0;

  return (
    <group ref={groupRef} position={radiator.position} rotation={[0, 0, angle]} name={`group-radiator-${side}-${radiatorIndex}-wall-${wallIndex}`}>
      <group position={[0, 0, selectedZ]}>
        <Box
          args={[radiatorWidth, thickness, 1]}
          renderOrder={selectedRenderOrder}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            handlePointerEvents(e, onClick);
          }}
          onPointerEnter={(e) => handlePointerEvents(e, () => setHovered(true))}
          onPointerLeave={(e) => handlePointerEvents(e, () => setHovered(false))}
        >
          <meshBasicMaterial attach="material" color={meshColor} />
          <Edges color="black" renderOrder={selectedRenderOrder} />
        </Box>
        {verticalLinePositions.map((position, index) => (
          <Line
            key={`line-${index}`}
            points={[new THREE.Vector3(position.x, -thickness / 2, 0), new THREE.Vector3(position.x, thickness / 2, 0)]}
            color="#444444"
            linewidth={0.8}
            depthTest={false}
            renderOrder={selectedRenderOrder}
          />
        ))}
        {(hovered || selected || showRadiatorLength) && <RadiatorLengthIndicator radiatorWidth={radiatorWidth} rotation={angle} color={'#444444'} />}
      </group>
    </group>
  );
};

export default memo(Radiator);
